import React, { useRef, useState } from "react";
import { Image as ImageIcon, Upload, X, Loader2 } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Per-track artwork uploader. Shows a tiny preview thumbnail plus an
 *  upload trigger and (when artwork is present) a remove button. Renders
 *  inline in the track list — small footprint, no modal needed. */
export default function TrackArtworkButton({ track, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const hasArt = !!track.cover_url;

  const pick = () => fileRef.current?.click();

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { setErr("Image must be under 3 MB."); e.target.value = ""; return; }
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(
        `/tracks/${track.track_id}/cover`,
        fd,
        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } },
      );
      onChange?.(data.cover_url);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!hasArt) return;
    if (!window.confirm("Remove artwork from this track?")) return;
    setBusy(true);
    try {
      await api.delete(`/tracks/${track.track_id}/cover`, { headers: authHeaders() });
      onChange?.(null);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Remove failed");
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-2" data-testid={`track-art-${track.track_id}`}>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onPick}
        data-testid={`track-art-input-${track.track_id}`}
      />
      {hasArt ? (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="relative w-12 h-12 rounded-md overflow-hidden border border-white/10 group flex-shrink-0"
          aria-label="Change artwork"
          data-testid={`track-art-thumb-${track.track_id}`}
          title="Click to replace artwork"
        >
          <img src={resolveAudio(track.cover_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 flex items-center justify-center transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Upload className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />}
          </div>
        </button>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="w-12 h-12 rounded-md border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.06] flex items-center justify-center text-white/40 hover:text-white/80 flex-shrink-0"
          aria-label="Add artwork"
          data-testid={`track-art-add-${track.track_id}`}
          title="Add artwork to this track"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        </button>
      )}
      {hasArt && !busy && (
        <button
          type="button"
          onClick={remove}
          className="text-white/30 hover:text-rose-400"
          aria-label="Remove artwork"
          data-testid={`track-art-remove-${track.track_id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {err && <div className="text-xs text-rose-400" data-testid={`track-art-err-${track.track_id}`}>{err}</div>}
    </div>
  );
}
